import type { ReactNode } from "react";
import { navigate } from "../lib/url-filters.js";

/**
 * An in-app link that is still a link.
 *
 * A real `<a href>`, so the browser gives it everything an anchor gets for
 * free: middle-click and cmd-click open a new tab, the status bar shows where
 * it goes, right-click can copy the address, and a screen reader announces it
 * as a link rather than as a button that happens to move you.
 *
 * A plain left-click is intercepted and handled in place, because a full page
 * load here would throw away the query cache and re-run the session check to
 * arrive at the same screen. Anything with a modifier is the person
 * deliberately asking for a new tab, and hijacking that is the classic
 * single-page-app annoyance — so the guard passes those straight through.
 *
 * The modifier check is kept in one place; it is easy to leave out and impossible to notice until someone tries.
 */
export function AppLink({
  href,
  className,
  children,
  onNavigate,
  ...rest
}: {
  readonly href: string;
  readonly className?: string;
  readonly children: ReactNode;
  readonly "aria-current"?: "page" | undefined;
  /** After an in-place move, e.g. to close the menu the link sat in. */
  readonly onNavigate?: () => void;
}) {
  return (
    <a
      href={href}
      {...(className === undefined ? {} : { className })}
      {...rest}
      onClick={(event) => {
        if (event.metaKey || event.ctrlKey || event.shiftKey || event.button !== 0) {
          return;
        }
        event.preventDefault();
        navigate(href);
        onNavigate?.();
      }}
    >
      {children}
    </a>
  );
}
